import "./PortfolioSidebarComponent.scss";
import {IComponentOptions, IOnDestroy, IOnInit} from "angular";
import {ComponentCallback} from "../../types/AngularJS";
import {StrategyService} from "../../types/StrategyService";
import {Strategy} from "../../strategybuilder/models/Strategy";
import {StateService} from "@uirouter/angularjs";
import {Optional} from "../../js/util/Optional";
import {StringKeyed} from "../../types/GenericTypes";
import {QuickWhatIfService} from "../service/QuickWhatIfService";
import {Subscription} from "rxjs/Subscription";
import {PortfolioPositions} from "../models/Portfolio";
import {CurrentUserService} from "../../services/user/CurrentUserService";
import {Position} from "../../types/Portfolio";
import {ArrayUtil} from "../../js/util/ArrayUtil";
import "rxjs/add/operator/take";
import "rxjs/add/operator/timeout";
import {ObjectUtil} from "../../js/util/ObjectUtil";

export const PortfolioSidebar = "portfolioSidebar";

export function portfolioSidebar(): IComponentOptions {
    return {
        templateUrl: "app/portfolio/sidebar/PortfolioSidebarComponent.html",
        bindings: {
            portfolio: "<",
            quickWhatIfService: "<",
            activeStrategyId: "<",
            onStrategyChange: "&",
            onWhatIfOpen: "&",
            onTradeStrategy: "&"
        },
        controllerAs: "ctrl",
        controller: PortfolioSidebarComponentController
    };
}

type StrategyWithMarginSummary = Strategy & { marginDetails?: any };

export class PortfolioSidebarComponentController implements IOnInit, IOnDestroy {

    public portfolio: PortfolioPositions;
    public quickWhatIfService: QuickWhatIfService;
    public onStrategyChange: ComponentCallback;
    public onWhatIfOpen: ComponentCallback;
    public onTradeStrategy: ComponentCallback;

    public activeStrategyId: number | null;
    public strategies: Array<StrategyWithMarginSummary>;
    public whatIfOpen = false;

    private quickWhatIfSubscription: Subscription = Subscription.EMPTY;

    /** @ngInject */
    public constructor(private strategyService: StrategyService,
                       private $state: StateService,
                       private confirmationService: ConfirmationService) {
    }

    public $onInit() {
        this.list();
        this.quickWhatIfSubscription = this.quickWhatIfService.subscribe(this.quickWhatIfChanged.bind(this));
    }

    private async list() {
        this.strategies = await this.strategyService.list();
        this.calculateStrategyMargins(this.strategies);
    }

    private async calculateStrategyMargins(strategies: Array<StrategyWithMarginSummary>) {
        if (strategies.length > 0) {
            const summaries = await this.strategyService.strategySummaries(this.portfolio.id, strategies);

            strategies.forEach((strat, index) => {
                const result = summaries.find(s => s.strategyId === strat.id);
                strat.marginDetails = {
                    costOfTrade: result!.costOfStrategy,
                    changeInMargin: result!.changeInMargin,
                    netEffect: result!.changeInBuyingPower
                };
            });
        }
    }

    public openQuickWhatIf(open: boolean) {
        this.whatIfIsOpen = open;
        // Reset the current strategy to nothing when opening and closing
        this.activeStrategyId = null;

        if (open) {
            // If there was a previous WhatIf then we use that value again,
            // otherwise we simply clear out any currently selected
            this.quickWhatIfService
                .timeout(1)
                .take(1)
                .subscribe(positions => this.quickWhatIfChanged(positions),
                           err => this.onStrategyChange({strategy: null}));
        } else {
            this.onStrategyChange({strategy: null});
        }
    }

    private set whatIfIsOpen(open: boolean) {
        this.whatIfOpen = open;
        this.onWhatIfOpen({isOpen: this.whatIfOpen});
    }

    private get whatIfIsOpen(): boolean {
        return this.whatIfOpen;
    }

    public toggleQuickWhatIf() {
        this.openQuickWhatIf(!this.whatIfOpen);
    }

    public editStrategy(strategy: Strategy) {
        this.$state.go("strategy", {activeStrategy: strategy.id});
    }

    public async deleteStrategy(strategy: Strategy) {
        await this.strategyService.deleteStrategy(strategy);
        if (strategy.id === this.activeStrategyId) {
            this.activeStrategyId = null;
            this.onStrategyChange({strategy: null});
        }
        this.list();
    }

    public async deleteAll() {
        const message = "Are you sure you want to clear all strategies?";
        await this.confirmationService.confirm("deleteAllStrategies", message);
        const requests = this.strategies.map((s) => this.strategyService.deleteStrategy(s));
        await Promise.all(requests);
        this.strategies = [];
        this.onStrategyChange({strategy: null});
    }

    public strategySelected(strategy: Strategy) {
        Optional.of(strategy)
                .map(s => s.id)
                .ifPresent(id => this.activeStrategyId = id);
        const copy = ObjectUtil.copy(strategy);
        this.onStrategyChange({ strategy: copy });
    }

    public tradeStrategy(strategy: Strategy) {
        this.openQuickWhatIf(false);
        this.onTradeStrategy({strategy});
    }

    public tradeWhatIf(whatIf: any) {
        this.openQuickWhatIf(false);
        this.onTradeStrategy({strategy: whatIf});
    }

    public quickWhatIfChanged(positions: Array<Position>) {
        if (!this.whatIfOpen) {
            this.whatIfIsOpen = true;
        }
        this.activeStrategyId = null;
        const whatIf = this.buildWhatIf(positions);
        this.onStrategyChange({strategy: whatIf});
    }

    private buildWhatIf(positions: Array<Position>): StringKeyed<any> {
        const whatIfTrades = ArrayUtil.clone(positions);
        return {
            trades: whatIfTrades
        };
    }

    public $onDestroy(): void {
        this.quickWhatIfSubscription.unsubscribe();
    }
}
